// Which floating rune labels a room's links should show, and where.
//
// Pure: objects in, labels out. No Phaser, no scene, no DOM -- which is what
// lets it be tested directly rather than by squinting at a canvas, and what
// keeps `GameScene` down to "draw what this says".
//
// Two things were wrong with the four near-identical passes this replaces
// (2026-08-16, both reported):
//
// **A link was only labelled on one of its two ends.** Each pass looped over
// `room.objects` and skipped anything not *carrying* the cluster, so the partner
// named by `sourceId` got nothing. For `Bind`·`Charge` and `Bind`·`Entropy` that
// is close to the worst possible place to lose half the picture: both were
// deliberately given their own colours *because* their two ends are nowhere near
// each other, and the comment saying so sat directly above the loop that only
// ever drew one of them. A link is a relationship between two loci and it now
// says so at both.
//
// **The stack was fixed rather than packed.** The four kinds were pinned at -4,
// -22, -40 and -58 so that an object carrying several links showed several
// legible labels instead of one pile. But almost nothing ever carries two, so in
// practice every Entropy link floated 58px -- most of two tiles -- above an
// object it was supposed to be labelling, to avoid a collision that was not
// happening. The offsets are now assigned per object from the links that object
// actually has, so one link always sits at -4 and only a genuinely stacked
// object spreads upward.
import { ObjectDef } from '../../rune-lab/src/sim/types';
import { RUNE_SYMBOL } from '../../rune-lab/src/runes/symbols';

/** One link cluster: what carries it, what it reads as, and how it is drawn.
 *
 * Ordered, and the order is the stacking order for an object that carries more
 * than one -- unchanged from the four passes this replaces, so a world that had
 * a stacked object still stacks it the same way round. */
export const LINK_KINDS = [
  { cluster: 'bind', concept: 'momentum', color: '#e08fd0' },
  { cluster: 'energyBind', concept: 'energy', color: '#e08fd0' },
  // Coloured apart from the two pinks above rather than with them: a live wire
  // and an Entropy coupling both have to be spottable from across a room, since
  // the whole point of each is that its two ends are not near each other.
  { cluster: 'chargeBind', concept: 'charge', color: '#6fd8e8' },
  { cluster: 'entropyBind', concept: 'entropy', color: '#9c7be0' },
] as const;

export type LinkCluster = (typeof LINK_KINDS)[number]['cluster'];

export interface LinkLabel {
  /** The object this label hangs over. */
  objectId: string;
  /** Stable identity for one label across refreshes: an object can carry (or be
   * named by) more than one kind at once. */
  key: string;
  cluster: LinkCluster;
  text: string;
  color: string;
  /** Pixels above the sprite's own top edge. */
  offsetY: number;
}

/** The first rung, and the gap between rungs. */
const BASE_OFFSET = 4;
const STACK_STEP = 18;

type Link = { sourceId?: string; mirrored?: boolean };

const linkOn = (obj: ObjectDef, cluster: LinkCluster): Link | undefined => (obj as unknown as Record<string, Link | undefined>)[cluster];

/** Every label the room's links call for.
 *
 * A link produces **two** labels -- one on the object carrying the cluster, one
 * on the partner it names -- except when the partner is the caster (`sourceId`
 * absent), who is not an object and has nothing to hang a label on. A link
 * naming something that is not in the room produces just the one, for the same
 * reason: there is nothing there to label.
 *
 * Both ends read the same symbols, `Reverse` included -- a link is one
 * relationship and its two ends should not look like two different things. The
 * `Reverse` glyph is a record of what was cast rather than a reading of what it
 * does: on an Entropy link it selects nothing (2026-08-16), and the other three
 * only ever captured it for this indicator. */
export function linkLabels(objects: ObjectDef[]): LinkLabel[] {
  const byId = new Map(objects.map((o) => [o.id, o]));
  // Collected per object first so the stack can be packed from what each object
  // actually ends up with, rather than from a table of what it might have had.
  const perObject = new Map<string, { cluster: LinkCluster; text: string; color: string }[]>();

  for (const kind of LINK_KINDS) {
    for (const obj of objects) {
      const link = linkOn(obj, kind.cluster);
      if (!link) continue;
      const text = RUNE_SYMBOL[kind.concept] + RUNE_SYMBOL.bind + (link.mirrored ? RUNE_SYMBOL.reverse : '');
      const entry = { cluster: kind.cluster, text, color: kind.color };
      const ends = [obj.id];
      // The far end. Skipped when it is the caster, when it is missing, and when
      // it is this object -- a self-link is guarded at cast time (`cast.ts`) but
      // a hand-authored room could still hold one, and two labels stacked on one
      // tile for a link to nowhere is not worth drawing.
      if (link.sourceId && link.sourceId !== obj.id && byId.has(link.sourceId)) ends.push(link.sourceId);
      for (const id of ends) perObject.set(id, [...(perObject.get(id) ?? []), entry]);
    }
  }

  const labels: LinkLabel[] = [];
  for (const [objectId, entries] of perObject) {
    entries.forEach((entry, index) => {
      labels.push({
        objectId,
        key: `${objectId}:${entry.cluster}`,
        cluster: entry.cluster,
        text: entry.text,
        color: entry.color,
        offsetY: BASE_OFFSET + index * STACK_STEP,
      });
    });
  }
  return labels;
}
